import { computed, inject } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withPaginated,
  withQuery,
} from '@kinet-open-network/client/shared/data-access/state';
import { ILocationPermission } from '@kinet-open-network/shared/data-access/model';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  debounceTime,
  filter,
  firstValueFrom,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { LocationPermission } from '../models/location-permission.model';
import { LocationsService } from '../services/locations.service';

export const LocationPermissionsStore = signalStore(
  { protectedState: false }, withCallState(),
  withState<{
    groupId: string | null;
    allAllowed: boolean;
  }>({
    groupId: null,
    allAllowed: false,
  }),
  withQuery({
    initial: { sort: ['isAllowed'] },
  }),
  withPaginated<LocationPermission>(),
  withMethods((store, locationsService = inject(LocationsService)) => ({
    loadPermissions: rxMethod<{
      query: ApiQuery | null;
      groupId: string | null;
    }>(
      pipe(
        debounceTime(100),
        tap(() => patchState(store, setLoading())),
        filter(({ groupId }) => !!groupId),
        switchMap(({ groupId, query }) =>
          locationsService.getLocationPermissions(
            groupId as string,
            query ?? {},
          ),
        ),
        tapResponse({
          next: (data) => patchState(store, { paginated: data }, setLoaded()),
          error: (error) => patchState(store, setError(error)),
        }),
      ),
    ),
    togglePermission: async (permission: LocationPermission) => {
      patchState(store, setLoading());
      const groupId = store.groupId();

      if (!groupId) {
        return;
      }

      const method = permission.isAllowed
        ? locationsService.disallowLocation
        : locationsService.allowLocation;

      const updated = await firstValueFrom(
        method.call(locationsService, groupId, permission.id).pipe(
          tapResponse({
            next: (data) => {
              patchState(
                store,
                (state) => ({
                  paginated: {
                    ...state.paginated!,
                    data: state.paginated!.data.map((p) =>
                      p.id === data.id ? data : p,
                    ),
                  },
                }),
                setLoaded(),
              );
            },
            error: (error) => patchState(store, setError(error)),
          }),
        ),
      );

      return updated;
    },
    toggleAccessType: async (data: ILocationPermission, groupId: string) => {
      patchState(store, setLoading());
      return await firstValueFrom(
        locationsService.toggleAccessType(data, groupId).pipe(
          tapResponse({
            next: () => patchState(store, setLoaded()),
            error: (err) => patchState(store, setError(err)),
          }),
        ),
      );
    },
    setGroupId: (groupId: string | null) => {
      patchState(store, { groupId });
    },
  })),
  withHooks({
    onInit: ({ loadPermissions, query, groupId }) => {
      loadPermissions(computed(() => ({ query: query(), groupId: groupId() })));
    },
  }),
);
